import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { ChevronDownIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import uuid from '../utils/uuid.js';
import Button from './Button.js';

var _excluded = ["className", "isExpanded", "sectionContentId"],
  _excluded2 = ["className", "header"],
  _excluded3 = ["as", "className"],
  _excluded4 = ["as", "children", "className", "contentId", "data-testid", "expanded", "onExpand", "padding", "preserveDom", "startExpanded"];

/*
  ExpandableSection.Toggle
*/
var ExpandableSectionToggle = function ExpandableSectionToggle(props) {
  var className = props.className,
    isExpanded = props.isExpanded,
    sectionContentId = props.sectionContentId,
    otherProps = _objectWithoutProperties(props, _excluded);
  var finalClasses = classnames('C-ExpandableSection__Toggle', {
    isExpanded: isExpanded
  }, className);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: finalClasses
  }, otherProps), /*#__PURE__*/React.createElement(Button, {
    "aria-expanded": isExpanded,
    "aria-controls": sectionContentId,
    "aria-label": "Expandable Section",
    className: "C-ExpandableSection__Toggle__Button --embeddedInComponent",
    size: "dense",
    iconOnly: true
  }, /*#__PURE__*/React.createElement(EnterpriseIcon, {
    icon: ChevronDownIcon
  })));
};
ExpandableSectionToggle.displayName = 'ExpandableSection.Toggle';

/*
  ExpandableSection.Header
*/
var ExpandableSectionHeader = function ExpandableSectionHeader(props) {
  var className = props.className,
    header = props.header,
    otherProps = _objectWithoutProperties(props, _excluded2);
  var finalClasses = classnames('C-ExpandableSection__TopRow__Header', className);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: finalClasses
  }, otherProps), header);
};
ExpandableSectionHeader.displayName = 'ExpandableSection.Header';

/*
  ExpandableSection.Content
*/
var ExpandableSectionContent = function ExpandableSectionContent(props) {
  var _props$as = props.as,
    As = _props$as === void 0 ? 'div' : _props$as,
    className = props.className,
    otherProps = _objectWithoutProperties(props, _excluded3);
  var finalClasses = classnames('C-ExpandableSection__ContentContainer', className);
  return /*#__PURE__*/React.createElement(As, _extends({
    className: finalClasses
  }, otherProps));
};
ExpandableSectionContent.displayName = 'ExpandableSection.Content';

/*
  ExpandableSection
*/
var ExpandableSection = function ExpandableSection(props) {
  var _props$as2 = props.as,
    As = _props$as2 === void 0 ? 'div' : _props$as2,
    children = props.children,
    className = props.className,
    _props$contentId = props.contentId,
    contentId = _props$contentId === void 0 ? uuid() : _props$contentId,
    dataTestId = props['data-testid'],
    expanded = props.expanded,
    onExpand = props.onExpand,
    _props$padding = props.padding,
    padding = _props$padding === void 0 ? 'normal' : _props$padding,
    _props$preserveDom = props.preserveDom,
    preserveDom = _props$preserveDom === void 0 ? false : _props$preserveDom,
    startExpanded = props.startExpanded,
    otherProps = _objectWithoutProperties(props, _excluded4);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDirty = _useState2[0],
    setIsDirty = _useState2[1];
  var _useState3 = useState(startExpanded || expanded || false),
    _useState4 = _slicedToArray(_useState3, 2),
    isExpanded = _useState4[0],
    setIsExpanded = _useState4[1];
  var isInitialMount = useRef(true);
  useEffect(function () {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Runs after component has mounted and initialized
      setIsExpanded(expanded);
    }
  }, [expanded]);
  var toggleClick = function toggleClick() {
    if (onExpand) {
      onExpand();
      setIsDirty(true);
    } else {
      setIsDirty(true);
      setIsExpanded(!isExpanded);
    }
  };
  if (!Array.isArray(children) || children.length !== 2 || children[0].type.displayName !== 'ExpandableSection.Content' && children[1].type.displayName !== 'ExpandableSection.Content') {
    throw new Error('Expandable Section requires ExpandableSectionContent and a header');
  }
  var expandableSectionContent;
  var expandableSectionHeader;
  if (children[0].type === ExpandableSectionContent) {
    expandableSectionContent = children[0];
    expandableSectionHeader = children[1];
  } else {
    expandableSectionContent = children[1];
    expandableSectionHeader = children[0];
  }
  expandableSectionContent = /*#__PURE__*/React.cloneElement(expandableSectionContent);
  expandableSectionHeader = /*#__PURE__*/React.cloneElement(expandableSectionHeader);
  var finalClasses = classnames('C-ExpandableSection', _defineProperty({
    '--block-animate': startExpanded && !isDirty || expanded && !isDirty,
    isExpanded: isExpanded
  }, "--padding-".concat(padding), padding), className);
  return /*#__PURE__*/React.createElement(As, _extends({
    className: finalClasses
  }, otherProps, {
    "data-testid": dataTestId ? dataTestId : null
  }), /*#__PURE__*/React.createElement("div", {
    className: "C-ExpandableSection__TopRow"
  }, /*#__PURE__*/React.createElement(ExpandableSection.Header, {
    onClick: toggleClick,
    header: expandableSectionHeader
  }), /*#__PURE__*/React.createElement(ExpandableSection.Toggle, {
    onClick: toggleClick,
    isExpanded: isExpanded,
    sectionContentId: contentId,
    "data-testid": dataTestId ? "".concat(dataTestId, "_toggleButton") : null
  })), preserveDom && /*#__PURE__*/React.createElement("div", {
    id: contentId,
    "aria-hidden": !isExpanded,
    "aria-expanded": isExpanded,
    className: "C-ExpandableSection__Content ".concat(isExpanded ? 'isVisible' : '')
  }, expandableSectionContent), isExpanded && !preserveDom && /*#__PURE__*/React.createElement("div", {
    id: contentId,
    "aria-hidden": !isExpanded,
    "aria-expanded": isExpanded,
    className: "C-ExpandableSection__Content ".concat(isExpanded ? 'isVisible' : '')
  }, expandableSectionContent));
};
ExpandableSection.displayName = 'ExpandableSection';
ExpandableSection.Toggle = ExpandableSectionToggle;
ExpandableSection.Header = ExpandableSectionHeader;
ExpandableSection.Content = ExpandableSectionContent;

export { ExpandableSectionContent, ExpandableSectionHeader, ExpandableSectionToggle, ExpandableSection as default };
